// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = {
  'Access-Control-Allow-Origin': '*',
  'Content-Type': 'application/json-patch+json',
};

// ============= Types =============

export interface UploadUrlDocumentsProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  urlCollection: string[];
  versionIfExists?: boolean;
  workflowId?: string;
  askSessionId?: string;
  collectionId?: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Uploads documents from URLs into a data room
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param urlCollection Array of URLs to upload
 * @param versionIfExists Optional - whether to version files if they exist
 * @param workflowId Optional workflow ID
 * @param askSessionId Optional ask session ID
 * @param collectionId Optional collection ID
 * @returns Promise with API response or error
 */
export const uploadUrlDocuments = async ({
  getAccessToken,
  workspaceId,
  urlCollection,
  versionIfExists,
  workflowId,
  askSessionId,
  collectionId,
}: UploadUrlDocumentsProps): Promise<any> => {
  try {
    console.log('🔄 Uploading documents from URLs...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // Build request body - always include these fields, use empty string if not provided
    const body: {
      urlCollection: string[];
      versionIfExists?: boolean;
      workflowId: string;
      askSessionId: string;
      collectionId: string;
    } = {
      urlCollection,
      workflowId: workflowId || '',
      askSessionId: askSessionId || '',
      collectionId: collectionId || '',
    };

    // Only include versionIfExists if it's explicitly set
    if (versionIfExists !== undefined) {
      body.versionIfExists = versionIfExists;
    }

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/urlupload`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify(body),
    });

    // Response handling
    if (!response.ok) {
      const errorText = await response.text();
      console.log(`HTTP error! status: ${response.status}`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // On successful 200 response, return data for workspace refresh
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`✅ Documents uploaded from URLs successfully`);
      return { success: true, data };
    }

    console.log(`✅ Documents uploaded from URLs successfully`);
    return { success: true };
  } catch (error) {
    console.error('🔴 Error uploading documents from URLs:', error);
    throw error;
  }
};
